import { useState } from "react";
import { useDispatch } from "react-redux";

import { HandbookExpenditureLocalChanges } from "pages/HandBook/model/slice";
import { templateExpendituresLocalEdit } from "pages/HandBook/model/thunks";

import { checkHasChangedFields, getChangedFields } from "utils/formHelpers/getChangedFields";

export const useTemplateAddExpenditureMaterials = ({
  sectionId,
  itemId,
  defaultMaterialsIds = [],
}: {
  sectionId: number;
  itemId: number;
  defaultMaterialsIds: number[];
}) => {
  const dispatch = useDispatch();
  const [materials, setMaterials] = useState<number[]>(defaultMaterialsIds);
  const [materialsFromHandbook, setMaterialsFromHandbook] = useState<Record<number, boolean>>(
    Object.fromEntries(defaultMaterialsIds.map((x) => [x, true]))
  );
  const [manualMaterials, setManualMaterials] = useState<Record<number, boolean>>({});

  const submitMaterials = (values: any) => {
    const newMaterials: HandbookExpenditureLocalChanges["newMaterials"] = [];
    const editedMaterials: HandbookExpenditureLocalChanges["editedMaterials"] = [];
    const deletedMaterials: HandbookExpenditureLocalChanges["deletedMaterials"] = [];
    Object.entries(values)
      .filter(([key, _]) => key.startsWith("id:"))
      .forEach(([key, value]) => {
        const id = Number(key.split(":")[1]);
        if (!value) {
          if (id > 0) {
            deletedMaterials.push(id);
          }
          // не обрабатываем удалённые материалы и те, которые были добавлены и удалены
          return;
        }
        const { _handbookExpenditure, ...materialFields } = value || ({} as any);
        if (!materialFields) return;
        const materialManualExpenditure = {
          id: materialFields.id,
          type: materialFields.expenditure_type,
          name: materialFields.name,
          measure: materialFields.measure,
          count: materialFields.count || 0,
          price: materialFields.price || 0,
        };

        // нет изменений материала по справочнику (для существующих материалов)
        const isUntouchedManualExpenditure =
          _handbookExpenditure?.manual_expenditure &&
          !checkHasChangedFields(
            {
              type: materialFields.expenditure_type,
              name: materialFields.name,
              measure: materialFields.measure,
            },
            _handbookExpenditure.manual_expenditure
          );

        if (isUntouchedManualExpenditure) {
          // нет изменений материала в шаблоне
          if (
            !checkHasChangedFields({ count: materialFields.count, price: materialFields.price }, _handbookExpenditure)
          ) {
            return;
          }
        }

        // для добавленных материалов
        const hasDifferenceWithHandbookVersion =
          _handbookExpenditure &&
          checkHasChangedFields(
            {
              type: materialFields.expenditure_type,
              name: materialFields.name,
              measure: materialFields.measure,
            },
            _handbookExpenditure.manual_expenditure || _handbookExpenditure
          );

        const payload = {
          handbook: isUntouchedManualExpenditure
            ? _handbookExpenditure.manual_expenditure.id
            : hasDifferenceWithHandbookVersion || !_handbookExpenditure?.id
            ? materialManualExpenditure
            : _handbookExpenditure.id,
          template: {
            id: id,
            section_id: sectionId,
            manual_expenditure: materialManualExpenditure,
            price: materialManualExpenditure.price,
            count: materialManualExpenditure.count,
          },
        };

        // Специальное поле _isNew для материалов, автоматически добавленных при выборе работы в автокомплите
        if (id < 0 || materialFields._isNew) {
          newMaterials.push(payload);
        } else {
          // @ts-ignore
          editedMaterials.push(payload);
        }
        if (_handbookExpenditure && !hasDifferenceWithHandbookVersion) {
          setManualMaterials((prev) => ({ ...prev, [id]: false }));
          setMaterialsFromHandbook((prev) => ({ ...prev, [id]: true }));
        } else {
          setManualMaterials((prev) => ({ ...prev, [id]: true }));
          setMaterialsFromHandbook((prev) => ({ ...prev, [id]: false }));
        }
      });
    if (newMaterials.length || deletedMaterials.length || editedMaterials.length) {
      dispatch(
        templateExpendituresLocalEdit({
          expenditureId: itemId,
          newMaterials,
          deletedMaterials,
          editedMaterials,
        })
      );
    }
  };

  return {
    submitMaterials,
    materialsFromHandbook,
    manualMaterials,
    materials,
    setMaterials,
  };
};
